import { Box, Typography, useTheme } from '@mui/material';
import type { UserAssertion } from '@/types';
import {
  formatAssertionReviewed,
  reviewedBallLabel,
  reviewedDiffAgainstOfficials,
  reviewedSanctionLabel,
} from '@/components/review/consensus';

/** Classic marker yellow — square edges; darker fill in dark mode for legible text. */
export function reviewedDiffHighlightSx(dark: boolean) {
  return {
    bgcolor: dark ? '#E6B800' : '#FFEB3B',
    color: dark ? '#1a1a1a' : 'inherit',
    fontWeight: 700,
    borderRadius: 0,
    px: 0.2,
    boxDecorationBreak: 'clone' as const,
  };
}

export function ComparedReviewedSpan({ label, highlight }: { label: string; highlight: boolean }) {
  const isDark = useTheme().palette.mode === 'dark';
  return (
    <Box component="span" sx={highlight ? reviewedDiffHighlightSx(isDark) : undefined}>
      {label}
    </Box>
  );
}

/** Table cell label (e.g. summary dialog 覆核球 / 覆核罰則 columns). */
export function ComparedReviewedPart({ label, highlight }: { label: string; highlight: boolean }) {
  return (
    <Typography variant="body2" fontWeight={600}>
      <ComparedReviewedSpan label={label} highlight={highlight} />
    </Typography>
  );
}

/** Inline reviewed outcome with per-part diff highlight vs official. */
export function ReviewedOutcomeDiffLine({
  assertion,
  ownerAssertion,
  ownerAssertions,
  highlightDiff,
}: {
  assertion: UserAssertion;
  ownerAssertion?: UserAssertion | null;
  ownerAssertions?: UserAssertion[];
  highlightDiff: boolean;
}) {
  if (!formatAssertionReviewed(assertion)) return null;

  const ball = reviewedBallLabel(assertion);
  const sanction = reviewedSanctionLabel(assertion);
  const officials =
    ownerAssertions?.length
      ? ownerAssertions
      : ownerAssertion
        ? [ownerAssertion]
        : [];
  const diff =
    highlightDiff && officials.length
      ? reviewedDiffAgainstOfficials(assertion, officials, true)
      : { ball: false, sanction: false };

  return (
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
      → <ComparedReviewedSpan label={ball} highlight={diff.ball} />
      {' · '}
      <ComparedReviewedSpan label={sanction} highlight={diff.sanction} />
    </Typography>
  );
}
